import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: string | null;
  username: string | null;
  permissions: { [key: string]: { can_read: boolean; can_write: boolean } };
  loading: boolean;
  login: (token: string, role: string, username: string, permissions: { [key: string]: { can_read: boolean; can_write: boolean } }) => void;
  logout: () => void;
  hasPermission: (resource: string, type: 'can_read' | 'can_write') => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<{ [key: string]: { can_read: boolean; can_write: boolean } }>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      const storedRole = localStorage.getItem('userRole');
      const storedUsername = localStorage.getItem('username');
      const storedPermissions = localStorage.getItem('permissions');

      if (token && storedRole && storedUsername && storedPermissions) {
        const decodedToken: any = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          setIsAuthenticated(true);
          setUserRole(storedRole);
          setUsername(storedUsername);
          setPermissions(JSON.parse(storedPermissions));
        } else {
          logout();
        }
      } 
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (
    token: string,
    role: string,
    user: string,
    perms: { [key: string]: { can_read: boolean; can_write: boolean } }
  ) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userRole', role);
    localStorage.setItem('username', user);
    localStorage.setItem('permissions', JSON.stringify(perms));
    setIsAuthenticated(true);
    setUserRole(role);
    setUsername(user);
    setPermissions(perms);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    localStorage.removeItem('permissions');
    setIsAuthenticated(false);
    setUserRole(null);
    setUsername(null);
    setPermissions({});
  };

  const hasPermission = (resource: string, type: 'can_read' | 'can_write'): boolean => {
    if (userRole === 'admin') return true; // Admins have all permissions
    return permissions[resource]?.[type] || false;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userRole,
        username,
        permissions,
        loading,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
